/**
 * Client-side push-prompt telemetry.
 *
 * Fires two ways for every prompt outcome:
 *   • the first-party ledger (`push_prompt_events`) used by the admin
 *     dashboard — route, source and time-to-prompt included;
 *   • a GA4 dataLayer event so the same funnel is visible in Analytics.
 */
import { getAnonymousId } from "@/lib/identity/anonymousId";
import { recordPushPromptEvent } from "./prompt-analytics.functions";

export type PushPromptEvent =
  | "prompted"
  | "granted"
  | "denied"
  | "dismissed"
  | "error"
  | "unsupported";

const GA_EVENT: Record<PushPromptEvent, string> = {
  prompted: "push_prompt_shown",
  granted: "push_opt_in",
  denied: "push_opt_out",
  dismissed: "push_prompt_dismissed",
  error: "push_prompt_error",
  unsupported: "push_prompt_unsupported",
};

/** ms since this document started — our "time to prompt". */
export function msSinceLanding(): number {
  if (typeof performance === "undefined") return 0;
  return Math.max(0, Math.round(performance.now()));
}

export function trackPushPrompt(
  event: PushPromptEvent,
  opts: { source: string; timeToPromptMs?: number; reason?: string },
): void {
  if (typeof window === "undefined") return;
  const route = window.location.pathname;
  const timeToPromptMs = opts.timeToPromptMs ?? msSinceLanding();

  try {
    const w = window as unknown as { dataLayer?: unknown[] };
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({
      event: GA_EVENT[event],
      push_prompt_source: opts.source,
      push_prompt_route: route,
      push_time_to_prompt_ms: timeToPromptMs,
      push_prompt_reason: opts.reason,
    });
  } catch {
    /* analytics is never load-bearing */
  }

  void recordPushPromptEvent({
    data: {
      event,
      source: opts.reason ? `${opts.source}:${opts.reason}`.slice(0, 40) : opts.source,
      route,
      timeToPromptMs,
      anonymousId: getAnonymousId() || undefined,
      userAgent: navigator.userAgent.slice(0, 400),
    },
  }).catch(() => {
    /* ignore */
  });
}

